import React, { useState } from 'react';
import type { FC } from 'react';
import styled, { useTheme } from 'styled-components';
import Typography from 'app/ui/components/atoms/typography';
import Button from 'app/ui/components/atoms/button';

import ErrorIcon from 'app/assets/error-icon.svg';

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
  max-width: 38rem;
  gap: 2rem;

  h3 {
    text-align: center;
    justify-content: center;
    line-height: 1em;
  }

  img {
    max-width: 3.75rem;

    ${({ theme: { media } }) => `
    ${media.large(
      () => `
      max-width: 5.5rem;
      `,
    )}

    `}
  }
`;

const ButtonWrapper = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  justify-content: center;
  align-items: center;
  padding: 0 6rem;

  ${({ theme: { media } }) => `
    ${media.small(
      () => `
      padding: 0;
      `,
    )}
  `}

  button {
    width: 100%;
    justify-content: center;
  }
`;

const DetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
`;

const ShowDetailsButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.knop};
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  padding: 0;
  text-align: left;
  
  &:hover {
    opacity: 0.8;
  }
`;

const DetailsContent = styled.div<{ $isOpen: boolean }>`
  display: ${({ $isOpen }) => ($isOpen ? 'block' : 'none')};
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.lightGray || '#f5f5f5'};
  border-radius: 4px;
  border-left: 3px solid ${({ theme }) => theme.colors.knop};
`;

interface ErrorComponentProps {
  details?: string;
}

const ErrorComponent: FC<ErrorComponentProps> = ({ details }) => {
  const theme = useTheme();
  const [showDetails, setShowDetails] = useState(false);

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  return (
    <ErrorWrapper>
      <img src={ErrorIcon} alt="Error Icon" />
      <Typography variant="h3">Oops! Something went wrong...</Typography>
      <Typography variant="body1" color={theme.colors.knop}>
        It looks like something didn't go as planned. We're working to fix and will be up and running soon.
      </Typography>

      {details && (
        <DetailsWrapper>
          <ShowDetailsButton onClick={toggleDetails}>
            <Typography variant="body2" color={theme.colors.knop}>
              {showDetails ? 'Hide details' : 'Show more details'}
            </Typography>
          </ShowDetailsButton>
          <DetailsContent $isOpen={showDetails}>
            <Typography variant="body2" color={theme.colors.black}>
              {details}
            </Typography>
          </DetailsContent>
        </DetailsWrapper>
      )}

      <ButtonWrapper>
        <Button
          type="primary"
          label="Back"
          onClick={() => {
            window.location.replace('/');
          }}
        />
      </ButtonWrapper>
    </ErrorWrapper>
  );
};

export default ErrorComponent;
